<template>
  <form v-if="!loading" class="billing-data-form">
    <section class="border-bottom">
      <h4>{{ __('Empresa') }}</h4>

      <div class="group">
        <div class="form-group">
          <label for="company-name">{{ __('Nome da empresa') }}</label>
          <input
            id="company-name"
            name="company-name"
            type="text"
            required
            :disabled="disabled"
            v-model="state.name"
          >
          <div v-if="hasErrors('name')" class="errors-container">
            <p v-for="error in getErrors('name')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="company-document">{{ __('CPF / CNPJ') }}</label>
          <Cleave
            id="company-document"
            name="company-document"
            type="text"
            required
            :disabled="disabled"
            v-model="state.document"
            :options="documentOptions"
          />
          <div v-if="hasErrors('document')" class="errors-container">
            <p v-for="error in getErrors('document')" class="text-danger">{{ error }}</p>
          </div>
        </div>
      </div>

    </section>
    <section class="border-bottom">
      <h4>{{ __('Responsável') }}</h4>

      <div class="group">
        <div class="form-group">
          <label for="contact-name">{{ __('Responsável') }}</label>
          <input
            id="contact-name"
            name="contact-name"
            type="text"
            required
            :disabled="disabled"
            v-model="state.contact"
          >
          <div v-if="hasErrors('contact')" class="errors-container">
            <p v-for="error in getErrors('contact')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="contact-document">{{ __('CPF do responsável') }}</label>
          <Cleave
            id="contact-document"
            name="contact-document"
            type="text"
            required
            :disabled="disabled"
            v-model="state.contact_document"
            :options="{
              numericOnly: true,
              blocks: [3, 3, 3, 2],
              delimiters: ['.', '.', '-'],
            }"
          />
          <div v-if="hasErrors('contact_document')" class="errors-container">
            <p v-for="error in getErrors('contact_document')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="contact-email">{{ __('E-mail') }}</label>
          <input
            id="contact-email"
            name="contact-email"
            type="email"
            required
            :disabled="disabled"
            v-model="state.email"
          >
          <div v-if="hasErrors('email')" class="errors-container">
            <p v-for="error in getErrors('email')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="contact-phone">{{ __('Telefone') }}</label>
          <Cleave
            id="contact-phone"
            name="contact-phone"
            type="text"
            required
            :disabled="disabled"
            v-model="state.contact_phone"
            :options="{
              numericOnly: true,
              blocks: [0, 2, 5, 4],
              delimiters: ['(', ') ', '-'],
            }"
          />
          <div v-if="hasErrors('contact_phone')" class="errors-container">
            <p v-for="error in getErrors('contact_phone')" class="text-danger">{{ error }}</p>
          </div>
        </div>
      </div>

    </section>
    <section>
      <h4>{{ __('Endereço') }}</h4>

      <div class="group">
        <div class="form-group">
          <label for="address-postalcode">{{ __('CEP') }}</label>
          <Cleave
            id="address-postalcode"
            name="address-postalcode"
            type="text"
            required
            :disabled="disabled"
            v-model="state.address.postalcode"
            :options="{
              numericOnly: true,
              blocks: [2, 3, 3],
              delimiters: ['.', '-'],
            }"
          />
          <div v-if="hasErrors('address.postalcode')" class="errors-container">
            <p v-for="error in getErrors('address.postalcode')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="address-street">{{ __('Rua') }}</label>
          <input
            id="address-street"
            name="address-street"
            type="text"
            required
            :disabled="disabled"
            :readonly="!state.address.postalcodeFail && !state.address.postalcodeGeneral"
            v-model="state.address.street"
          >
          <div v-if="hasErrors('address.street')" class="errors-container">
            <p v-for="error in getErrors('address.street')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="address-number">{{ __('Número') }}</label>
          <input
            id="address-number"
            name="address-number"
            type="text"
            required
            :disabled="disabled"
            v-model="state.address.number"
          >
          <div v-if="hasErrors('address.number')" class="errors-container">
            <p v-for="error in getErrors('address.number')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="address-complement">{{ __('Complemento') }}</label>
          <input
            id="address-complement"
            name="address-complement"
            type="text"
            :disabled="disabled"
            v-model="state.address.complement"
          >
          <div v-if="hasErrors('address.complement')" class="errors-container">
            <p v-for="error in getErrors('address.complement')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="address-district">{{ __('Bairro') }}</label>
          <input
            id="address-district"
            name="address-district"
            type="text"
            required
            :disabled="disabled"
            :readonly="!state.address.postalcodeFail && !state.address.postalcodeGeneral"
            v-model="state.address.district"
          >
          <div v-if="hasErrors('address.district')" class="errors-container">
            <p v-for="error in getErrors('address.district')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="address-city">{{ __('Cidade') }}</label>
          <input
            id="address-city"
            name="address-city"
            type="text"
            required
            :disabled="disabled"
            :readonly="!state.address.postalcodeFail"
            v-model="state.address.city"
          >
          <div v-if="hasErrors('address.city')" class="errors-container">
            <p v-for="error in getErrors('address.city')" class="text-danger">{{ error }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="address-state">{{ __('Estado') }}</label>
          <input
            id="address-state"
            name="address-state"
            type="text"
            required
            :disabled="disabled"
            :readonly="!state.address.postalcodeFail"
            v-model="state.address.state"
          >
          <div v-if="hasErrors('address.state')" class="errors-container">
            <p v-for="error in getErrors('address.state')" class="text-danger">{{ error }}</p>
          </div>
        </div>
      </div>

    </section>

    <button
      type="button"
      class="w-100 salesflow-btn salesflow-btn-primary"
      v-show="showButton"
      @click="() => $emit('advance', data)"
      :disabled="submitting || disabled"
    >
      {{ submitting ? __('Aguarde') + '...' : __('Avançar') }}
    </button>
  </form>
  <div
    v-else
    class="w-100 p-3 d-flex align-items-center justify-content-center"
  >
    <i class="fa fa-circle-o-notch fa-spin" />
  </div>
</template>

<script>
import Cleave from 'vue-cleave-component';

export default {
  components: { Cleave },

  props: {
    errors: {
      type: Object,
      default: () => ({}),
    },
    showButton: {
      type: Boolean,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: true,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      state: {
        name: '',
        document: '',
        contact: '',
        contact_document: '',
        contact_phone: '',
        email: '',
        address: {
          number: '',
          street: '',
          postalcode: '',
          city: '',
          state: '',
          district: '',
          complement: '',
          postalcodeFail: false,
          postalcodeGeneral: false,
        },
      },
      loading: false,
    };
  },

  updated () {
    this.$emit('updated', this.data);
  },

  watch: {
    'state.address.postalcode': function (postalcode) {
      console.log(postalcode);
      if (this.loading || postalcode?.length < 8) {
        return;
      }

      this.setAddressFromPostalcode(postalcode);
    },
  },

  computed: {
    documentOptions () {
      if (this.state.document?.length > 11) {
        // cnpj
        return {
          numericOnly: true,
          blocks: [2, 3, 3, 4, 2],
          delimiters: ['.', '.', '/', '-'],
        };
      }

      // cpf
      return {
        numericOnly: true,
        blocks: [3, 3, 3, 3],
        delimiters: ['.', '.', '-'],
      };
    },

    data () {
      return {
        name: this.state.name,
        document: this.state.document,
        contact: this.state.contact,
        contact_document: this.state.contact_document,
        contact_phone: this.state.contact_phone,
        email: this.state.email,
        address: {
          number: this.state.address.number,
          street: this.state.address.street,
          postalcode: this.state.address.postalcode,
          city: this.state.address.city,
          state: this.state.address.state,
          district: this.state.address.district,
          complement: this.state.address.complement,
        },
      }
    },
  },

  created () {
    this.loading = true;

    this.$http.get('/billing/details').then(({ data }) => {
      if (!data.tenant.address) {
        data.tenant.address = {};
      }

      this.state = {
        ...this.state,
        ...data.tenant
      };
    }).catch(() => {
      Alert.error('Falha ao buscar dados de faturamento!');
    }).finally(() => {
      this.loading = false;
    });
  },

  methods: {
    hasErrors(key) {
      const errors = this.getErrors(key);

      if (!errors) {
        return false;
      }

      return !!errors.length;
    },

    getErrors(key) {
      return this.errors[key];
    },

    setAddressFromPostalcode (postalcode) {
      this.$http.get(`/address/${postalcode}`)
        .then(({ data }) => {
          this.state.address.postalcode = data.postalcode;
          this.state.address.city = data.city;
          this.state.address.state = data.state;
          this.state.address.street = data.street;
          this.state.address.district = data.district;

          this.state.address.postalcodeFail = false;
          this.state.address.postalcodeGeneral = false;

          if (this.state.address.street == '' || this.state.address.district == '') {
            this.state.address.postalcodeGeneral = true;
          }

          this.$forceUpdate();
        })
        .catch(() => {
          this.state.address.postalcodeFail = true;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../sass/variables';
@import '../../../sass/mixins';

.billing-data-form {
  .border-bottom {
    border-bottom: 1px solid $salesflow-outline-gray-medium;
  }

  .salesflow-btn:disabled {
    cursor: not-allowed;
  }

  section {
    display: flex;
    flex-direction: column;
    padding: 1.5rem 0rem 3rem;
    gap: 1.5rem;

    h4 {
      @include salesflow-subtitle($font-weight: 500);
      font-size: 1.25rem;
      margin-bottom: 0px;
    }

    .group {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 2rem;

      .form-group {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-bottom: 0px;

        label {
          @include salesflow-text();
          margin-bottom: 0px;
        }

        input {
          width: 100%;
          padding: 0.75rem;
          background: white;
          border: 1px solid $salesflow-outline-gray-medium;
          border-radius: 8px;

          @include salesflow-subtitle($color: $salesflow-font-dark);

          &:focus {
            box-shadow: 0px 0px 0px 4px #0D6EFD40 !important;
            border-color: $salesflow-outline-gray-medium !important;
          }

          transition: box-shadow 0.25s;
        }
      }
    }
  }
}

</style>
